import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Send, Wrench } from "lucide-react";
import type { OrderType, OrderStatus, FulfillmentStatus, NotificationStatus, Customer, LineItem, RepairStatus } from "@sbd-tcic/shared-types";
import { api, ApiClientError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";
import { StatusBadge } from "../components/StatusBadge";

interface RepairOrderSummary {
  id: string;
  equipment: string;
  modelNumber: string;
  serialNumber: string;
  status: RepairStatus;
  technician: string | null;
  promisedDate: string;
  deliveryMethod: string;
  toolFailure: string;
}

interface OrderDetailDto {
  orderNumber: string;
  orderType: OrderType;
  orderMethod: string;
  serviceType: string;
  orderDate: string;
  customerId: string;
  value: number;
  status: OrderStatus;
  fulfillmentStatus: FulfillmentStatus;
  paymentMethod: string;
  notificationStatus: NotificationStatus;
  createdBy: string;
  reason: string;
  customerName: string;
  customer: Customer | null;
  lineItems?: LineItem[];
  repairOrder?: RepairOrderSummary | null;
}

type NotifyChannel = "email" | "sms" | "print";

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function OrderDetail() {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { toast } = useToast();

  const [order, setOrder] = useState<OrderDetailDto | null>(null);
  const [loading, setLoading] = useState(true);

  const [channel, setChannel] = useState<NotifyChannel>("email");
  const [message, setMessage] = useState("");
  const [notifying, setNotifying] = useState(false);

  const load = useCallback(async () => {
    if (!orderNumber) return;
    setLoading(true);
    try {
      const data = await api.get<OrderDetailDto>(`/orders/${encodeURIComponent(orderNumber)}`);
      setOrder(data);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to load this order.", "error");
    } finally {
      setLoading(false);
    }
  }, [orderNumber, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function sendNotification() {
    if (!orderNumber || !message.trim()) return;
    setNotifying(true);
    try {
      const result = await api.post<{ sent: boolean; reason?: string }>(`/orders/${encodeURIComponent(orderNumber)}/notify`, {
        channel,
        message: message.trim(),
      });
      if (result.sent) {
        toast(`Notification queued via ${channel}.`, "success");
        setMessage("");
        await load();
      } else if (result.reason === "not_opted_in") {
        toast("Customer has not opted in to this notification channel — nothing was sent.", "info");
      } else {
        toast("Notification was not sent.", "info");
      }
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to send notification.", "error");
    } finally {
      setNotifying(false);
    }
  }

  if (loading || !order) return <div className="text-ink-muted text-sm py-10 text-center">Loading order…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5">
      <div className="flex flex-col gap-5">
        <div className="card p-5">
          <div className="flex items-start justify-between mb-3.5 flex-wrap gap-2">
            <div>
              <div className="text-lg font-extrabold">{order.orderNumber}</div>
              <div className="text-sm text-ink-muted">
                {order.customerName || "Unknown customer"} · {order.orderType}
              </div>
            </div>
            <div className="flex gap-1.5 flex-wrap justify-end">
              <StatusBadge status={order.status} />
              <StatusBadge status={order.fulfillmentStatus} />
              <StatusBadge status={order.notificationStatus} />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-ink-muted text-xs">Order Method</div>
              <div className="font-semibold">{order.orderMethod}</div>
            </div>
            <div>
              <div className="text-ink-muted text-xs">Service Type</div>
              <div className="font-semibold">{order.serviceType}</div>
            </div>
            <div>
              <div className="text-ink-muted text-xs">Order Date</div>
              <div className="font-semibold">{order.orderDate.slice(0, 10)}</div>
            </div>
            <div>
              <div className="text-ink-muted text-xs">Value</div>
              <div className="font-semibold">{money(order.value)}</div>
            </div>
            <div>
              <div className="text-ink-muted text-xs">Payment Method</div>
              <div className="font-semibold">{order.paymentMethod}</div>
            </div>
            <div>
              <div className="text-ink-muted text-xs">Created By</div>
              <div className="font-semibold">{order.createdBy}</div>
            </div>
          </div>
          {order.reason && (
            <div className="mt-3.5 text-sm">
              <div className="text-ink-muted text-xs">Reason</div>
              <div>{order.reason}</div>
            </div>
          )}
        </div>

        {order.customer && (
          <div className="card p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3">Customer</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-ink-muted text-xs">Name</div>
                <div className="font-semibold">{order.customer.name}</div>
              </div>
              <div>
                <div className="text-ink-muted text-xs">Account #</div>
                <div className="font-semibold">{order.customer.customerNumber}</div>
              </div>
              <div>
                <div className="text-ink-muted text-xs">Email</div>
                <div className="font-semibold">{order.customer.email || "—"}</div>
              </div>
              <div>
                <div className="text-ink-muted text-xs">Phone</div>
                <div className="font-semibold">{order.customer.phone || "—"}</div>
              </div>
              <div>
                <div className="text-ink-muted text-xs">Email Opt-In</div>
                <div className="font-semibold">{order.customer.notificationOptInEmail ? "Yes" : "No"}</div>
              </div>
              <div>
                <div className="text-ink-muted text-xs">SMS Opt-In</div>
                <div className="font-semibold">{order.customer.notificationOptInSms ? "Yes" : "No"}</div>
              </div>
            </div>
          </div>
        )}

        {order.lineItems && (
          <div className="card p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3">Line Items</h2>
            {order.lineItems.length === 0 ? (
              <div className="text-sm text-ink-muted text-center py-4">No line items on this order.</div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {order.lineItems.map((l) => (
                  <div key={l.partNumber} className="flex items-center justify-between text-sm border-b border-border last:border-0 py-1.5">
                    <div>
                      <div className="font-semibold">
                        {l.partNumber} — {l.description}
                      </div>
                      <div className="text-xs text-ink-muted">
                        {l.qty} × {money(l.yourPrice)} · {l.deliveryMethod}
                      </div>
                    </div>
                    <div className="font-bold">{money(l.extPrice)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {order.repairOrder && (
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3.5">
              <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted flex items-center gap-2">
                <Wrench size={15} /> Linked Repair Order
              </h2>
              <StatusBadge status={order.repairOrder.status} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm mb-3.5">
              <div>
                <div className="text-ink-muted text-xs">Equipment</div>
                <div className="font-semibold">
                  {order.repairOrder.equipment} ({order.repairOrder.modelNumber})
                </div>
              </div>
              <div>
                <div className="text-ink-muted text-xs">Serial #</div>
                <div className="font-semibold">{order.repairOrder.serialNumber}</div>
              </div>
              <div>
                <div className="text-ink-muted text-xs">Technician</div>
                <div className="font-semibold">{order.repairOrder.technician ?? "Unassigned"}</div>
              </div>
              <div>
                <div className="text-ink-muted text-xs">Promised Date</div>
                <div className="font-semibold">{order.repairOrder.promisedDate}</div>
              </div>
              <div>
                <div className="text-ink-muted text-xs">Delivery Method</div>
                <div className="font-semibold">{order.repairOrder.deliveryMethod}</div>
              </div>
            </div>
            <button className="btn-outline" onClick={() => navigate(`/repair-orders/${order.repairOrder!.id}`)}>
              View Repair Order
            </button>
          </div>
        )}
      </div>

      {can("order_history.notify") && (
        <div className="card p-5 h-fit sticky top-20">
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3.5 flex items-center gap-2">
            <Send size={15} /> Notify Customer
          </h2>
          <label className="field-label">Channel</label>
          <select className="input mb-3" value={channel} onChange={(e) => setChannel(e.target.value as NotifyChannel)}>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="print">Print</option>
          </select>
          <label className="field-label">Message</label>
          <textarea
            className="input mb-3.5"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Message to send to the customer…"
          />
          <button className="btn-primary w-full justify-center" disabled={notifying || !message.trim()} onClick={() => void sendNotification()}>
            {notifying ? "Sending…" : "Send Notification"}
          </button>
          <p className="text-[11px] text-ink-muted mt-2.5">
            Only sent if the customer has opted in to this channel (BR-006). The backend enforces this automatically.
          </p>
        </div>
      )}
    </div>
  );
}
